import React, { useState } from 'react'

/* ─────────────────────────────────────────────────────────────
   Request History
   Per brief:
   - Active requests at the top, clearly marked In Progress
   - Previous requests below: service type, date, status only
   - Statuses: Received · In Progress · Confirmed · Completed
   - No pricing displayed anywhere
   - Clicking a request shows full detail view
───────────────────────────────────────────────────────────── */

/* Replace with Laravel API response */
const MOCK_ALL_REQUESTS = [
  {
    id: 101,
    service:  'Private Aviation',
    type:     'aviation',
    detail:   'BRU → DXB',
    date:     '14 Apr 2026',
    status:   'In Progress',
    ref:      'REQ-101',
    submitted:'14 Apr 2026, 09:12',
    notes:    'Business class. 2 adults, 1 infant. Flexible ±2 days.',
  },
  {
    id: 102,
    service:  'Airport Services',
    type:     'airport',
    detail:   'Arrival meet & greet — BRU',
    date:     '18 Apr 2026',
    status:   'Received',
    ref:      'REQ-102',
    submitted:'15 Apr 2026, 14:30',
    notes:    'Flight SN601. 2 passengers. Executive Sedan.',
  },
  {
    id: 98,
    service:  'Partner Hotels',
    type:     'hotels',
    detail:   'Dubai — Atlantis The Royal',
    date:     '02–07 Apr 2026',
    status:   'Confirmed',
    ref:      'REQ-098',
    submitted:'28 Mar 2026, 11:05',
    notes:    'Suite. 2 guests. High floor, complete privacy.',
  },
  {
    id: 95,
    service:  'Car Transfer',
    type:     'transfers',
    detail:   'Hotel Amigo → BRU Airport',
    date:     '10 Apr 2026',
    status:   'Completed',
    ref:      'REQ-095',
    submitted:'08 Apr 2026, 08:44',
    notes:    'Pickup 06:30. 1 passenger. Maybach.',
  },
  {
    id: 91,
    service:  'Private Aviation',
    type:     'aviation',
    detail:   'DXB → BRU',
    date:     '09 Apr 2026',
    status:   'Completed',
    ref:      'REQ-091',
    submitted:'05 Apr 2026, 16:20',
    notes:    'First class. 1 adult. Special dietary requirements.',
  },
  {
    id: 87,
    service:  'Car Transfer',
    type:     'transfers',
    detail:   'BRU Airport → Residence',
    date:     '01 Apr 2026',
    status:   'Completed',
    ref:      'REQ-087',
    submitted:'30 Mar 2026, 22:10',
    notes:    '2 passengers. Executive SUV.',
  },
]

const STATUS_ORDER = ['In Progress', 'Received', 'Confirmed', 'Completed']

const STATUS_CLASS = {
  'In Progress': 'db-badge--progress',
  'Received':    'db-badge--received',
  'Confirmed':   'db-badge--confirmed',
  'Completed':   'db-badge--completed',
}

const SERVICE_FILTERS = [
  { id: 'all',       label: 'All'             },
  { id: 'aviation',  label: 'Aviation'        },
  { id: 'hotels',    label: 'Hotels'          },
  { id: 'transfers', label: 'Transfers'       },
  { id: 'airport',   label: 'Airport'         },
]

/* ─────────────────────────────────────────────────────────────
   DETAIL PANEL
───────────────────────────────────────────────────────────── */
function RequestDetail({ request, onClose }) {
  return (
    <div className="rh-detail">
      <div className="rh-detail__header">
        <button className="cnt-back" onClick={onClose} aria-label="Back to history">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          All Requests
        </button>
        <div className="rh-detail__meta">
          <span className="rh-detail__ref">{request.ref}</span>
          <span className={`db-badge ${STATUS_CLASS[request.status]}`}>{request.status}</span>
        </div>
      </div>

      <div className="rh-detail__body">
        <p className="cnt-header__overline">Request Detail</p>
        <h2 className="rh-detail__title">{request.service}</h2>
        <p className="rh-detail__subtitle">{request.detail}</p>

        <div className="rh-detail__rows">
          <div className="rh-detail__row">
            <span className="rh-detail__key">Reference</span>
            <span className="rh-detail__val">{request.ref}</span>
          </div>
          <div className="rh-detail__row">
            <span className="rh-detail__key">Date</span>
            <span className="rh-detail__val">{request.date}</span>
          </div>
          <div className="rh-detail__row">
            <span className="rh-detail__key">Submitted</span>
            <span className="rh-detail__val">{request.submitted}</span>
          </div>
          <div className="rh-detail__row">
            <span className="rh-detail__key">Status</span>
            <span className="rh-detail__val">
              <span className={`db-badge ${STATUS_CLASS[request.status]}`}>{request.status}</span>
            </span>
          </div>
          <div className="rh-detail__row">
            <span className="rh-detail__key">Details</span>
            <span className="rh-detail__val">{request.notes}</span>
          </div>
        </div>

        {/* Status trail */}
        <div className="rh-trail">
          {STATUS_ORDER.filter(s => s !== 'Completed').concat(['Completed']).map((s, i, arr) => {
            const statusIdx  = STATUS_ORDER.indexOf(request.status)
            const thisIdx    = arr.indexOf(s)
            const isDone     = thisIdx <= statusIdx
            const isCurrent  = s === request.status
            return (
              <React.Fragment key={s}>
                <div className={`rh-trail__step${isDone ? ' rh-trail__step--done' : ''}${isCurrent ? ' rh-trail__step--current' : ''}`}>
                  <div className="rh-trail__dot" aria-hidden="true" />
                  <span className="rh-trail__label">{s}</span>
                </div>
                {i < arr.length - 1 && (
                  <div className={`rh-trail__line${isDone && thisIdx < statusIdx ? ' rh-trail__line--done' : ''}`} aria-hidden="true" />
                )}
              </React.Fragment>
            )
          })}
        </div>

        <p className="rh-detail__notice">
          No pricing is displayed. Our team will be in touch with details of your arrangement.
        </p>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
export default function RequestHistory({ onBack }) {
  const [filter,   setFilter]   = useState('all')
  const [selected, setSelected] = useState(null)

  if (selected) {
    return (
      <div className="db-page cnt-page cnt-page--wide">
        <RequestDetail request={selected} onClose={() => setSelected(null)} />
      </div>
    )
  }

  const active   = MOCK_ALL_REQUESTS.filter(r =>
    ['In Progress', 'Received'].includes(r.status) &&
    (filter === 'all' || r.type === filter)
  )
  const previous = MOCK_ALL_REQUESTS.filter(r =>
    !['In Progress', 'Received'].includes(r.status) &&
    (filter === 'all' || r.type === filter)
  )

  return (
    <div className="db-page cnt-page cnt-page--wide">

      {/* Header */}
      <div className="cnt-header">
        <button className="cnt-back" onClick={onBack} aria-label="Back">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Dashboard
        </button>
        <div className="cnt-header__title-wrap">
          <p className="cnt-header__overline">Member</p>
          <h1 className="cnt-header__title">Request History</h1>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="rh-filters" role="tablist" aria-label="Filter by service">
        {SERVICE_FILTERS.map(f => (
          <button
            key={f.id}
            role="tab"
            aria-selected={filter === f.id}
            className={`rh-filter${filter === f.id ? ' rh-filter--active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Active requests */}
      {active.length > 0 && (
        <section className="rh-section" aria-label="Active requests">
          <p className="db-overline">Active</p>
          <div className="rh-list">
            {active.map((req, i) => (
              <button
                key={req.id}
                className="rh-row rh-row--active"
                onClick={() => setSelected(req)}
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <div className="rh-row__left">
                  <span className="rh-row__service">{req.service}</span>
                  <span className="rh-row__detail">{req.detail}</span>
                </div>
                <div className="rh-row__right">
                  <span className="rh-row__date">{req.date}</span>
                  <span className={`db-badge ${STATUS_CLASS[req.status]}`}>{req.status}</span>
                  <span className="rh-row__caret" aria-hidden="true">›</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Previous requests */}
      {previous.length > 0 && (
        <section className="rh-section" aria-label="Previous requests">
          <p className="db-overline">Previous</p>
          <div className="rh-list">
            {previous.map((req, i) => (
              <button
                key={req.id}
                className="rh-row"
                onClick={() => setSelected(req)}
                style={{ animationDelay: `${(active.length + i) * 50}ms` }}
              >
                <div className="rh-row__left">
                  <span className="rh-row__service">{req.service}</span>
                  <span className="rh-row__detail">{req.detail}</span>
                </div>
                <div className="rh-row__right">
                  <span className="rh-row__date">{req.date}</span>
                  <span className={`db-badge ${STATUS_CLASS[req.status]}`}>{req.status}</span>
                  <span className="rh-row__caret" aria-hidden="true">›</span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {active.length === 0 && previous.length === 0 && (
        <div className="rh-empty">
          <p className="rh-empty__text">No requests found.</p>
        </div>
      )}

    </div>
  )
}